import { theme } from "@lvt/material";
import { TextField, Button, Typography, Tooltip } from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { ThemeProvider } from "@material-ui/styles";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useState, useEffect, useRef, useContext, useMemo } from "react";

import ArchiveTimeline from "@/common/components/ArchivePage/ArchiveTimeline/ArchiveTimeline";
import { useStyles, pickerStyles, tzStyle } from "@/common/components/ArchivePage/DateTimeHeader/DateTimeHeaderStyles";
import { LiveUnitMetadataContext } from "@/common/contexts/LiveUnitMetadataContext";
import {
    setVideoSelectionMode,
    setGridAndAccordionWidth,
    setDateTimeHeaderHeight,
    setTimelineStart,
} from "@/store/archives/slice";
import { useDispatch, RootState, useSelector } from "@/store/index";
import { setHidden } from "@/store/liveUnitNav/slice";
import * as FullStory from "@/utilities/hooks/fullstory";
import { ArchiveImagesNavMeta } from "@/utilities/types/archive";

import { VideoRequestHeader } from "../VideoRequestHeader/VideoRequestHeader";


export default function DateTimeHeader() {
    const gridRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const setAccordionWidth = () => {
        const gridDateTimeHeaderWidth = gridRef.current?.offsetWidth || 0;

        dispatch(setGridAndAccordionWidth(gridDateTimeHeaderWidth * 0.9)); //multiplying by .9 to compensate for set horizontal padding of (5%)
    };
    const setHeaderHeight = () => {
        const headerHeight = headerRef.current?.offsetHeight || 0;

        dispatch(setDateTimeHeaderHeight(headerHeight));
    };

    useEffect(() => {
        window.addEventListener("load", setAccordionWidth);
        window.addEventListener("load", setHeaderHeight);
        window.addEventListener("resize", setAccordionWidth);

        setHeaderHeight();
        setAccordionWidth();

        return () => {
            window.removeEventListener("load", setAccordionWidth);
            window.removeEventListener("resize", setAccordionWidth);
        };
    }, []);

    const unitMetadata = useContext<ArchiveImagesNavMeta>(LiveUnitMetadataContext);
    const timelineStartString: string = useSelector((state: RootState) => state.archives.timelineStart);
    const timelineStart: DateTime = useMemo(
        () => DateTime.fromISO(timelineStartString, { zone: unitMetadata.timezone }),
        [timelineStartString, unitMetadata],
    );
    const canRequestVideo: boolean = useSelector((state: RootState) => state.archives.canRequestVideo);

    const videoSelectionModeActive: boolean = useSelector((state: RootState) => state.archives.videoSelectionMode);
    const [dateInput, setDateInput] = useState<DateTime | null>(timelineStart.plus({ minutes: 30 }));
    const [timeInput, setTimeInput] = useState<DateTime | null>(timelineStart.plus({ minutes: 30 })); // pull timezone fromm api response which will be set in store

    useEffect(() => {
        setDateInput(timelineStart.plus({ minutes: 30 }));
        setTimeInput(timelineStart.plus({ minutes: 30 }));
    }, [timelineStart]);

    const classes = useStyles();

    const openVideoSelection = () => {
        dispatch(setVideoSelectionMode(true));
        dispatch(setHidden(true));
    };

    const outsideOfBounds = (): boolean => {
        let tooEarly: boolean;
        let afterNow: boolean;

        if (unitMetadata.recordingStart) {
            tooEarly =
                DateTime.fromISO(unitMetadata.recordingStart, { zone: unitMetadata.timezone }).diff(
                    timelineStart,
                    "seconds",
                ).seconds > 0;
            afterNow = timelineStart.diff(DateTime.now().setZone(unitMetadata.timezone), "seconds").seconds > 0;
        } else {
            tooEarly = DateTime.now().minus({ days: 90 }).diff(timelineStart, "seconds").seconds < 0;
            afterNow = timelineStart.diff(DateTime.now(), "seconds").seconds > 0;
        }

        return tooEarly || afterNow;
    };

    const showStatus = !outsideOfBounds() ? theme.palette.secondary.main : theme.palette.error.main;

    const renderRecordingStart = (): string => {
        if (!unitMetadata.recordingStart) {
            return DateTime.now().minus({ days: 90 }).toLocaleString(DateTime.DATETIME_FULL);
        }

        return DateTime.fromISO(unitMetadata.recordingStart)
            .setZone(unitMetadata.timezone)
            .toLocaleString(DateTime.DATETIME_FULL);
    };

    const updateTimelineStartDate = (pickersDate: DateTime | null) => {
        const date = pickersDate?.toISODate();
        const time = timeInput?.toISOTime({ includeOffset: true, includePrefix: true });
        const newDateTimeISO = `${date}${time}`;

        if (DateTime.fromISO(newDateTimeISO).isValid) {
            dispatch(setTimelineStart(`${date}${time}`));
        }
    };

    const updateTimelineStartTime = (pickersTime: DateTime | null) => {
        const date = dateInput?.toISODate();
        const time = pickersTime?.toISOTime({ includeOffset: true, includePrefix: true });

        const newDateTimeISO = `${date}${time}`;
        const newDateTime = DateTime.fromISO(newDateTimeISO);
        const test = newDateTime.minus({ minutes: 30 });

        if (test.isValid) {
            dispatch(setTimelineStart(test.toISO()));
        }
    };

    const handleDateChange = (date: MaterialUiPickersDate) => {
        if (date?.isValid) {
            updateTimelineStartDate(date);
        }

        setDateInput(date);
    };

    const handleTimeChange = (time: MaterialUiPickersDate) => {
        if (time) {
            // Keep the local time and change timezone (only for keyboard inputs)
            // Keyboard inputs give time in local timezone
            if (time.zoneName !== unitMetadata.timezone) {
                time = time.setZone(unitMetadata.timezone, { keepLocalTime: true });
            }

            let newDateTime;

            if (dateInput?.isValid) {
                const newDatetimeISO = `${dateInput?.toISODate()}${time.toISOTime({
                    includeOffset: true,
                    includePrefix: true,
                })}`;

                //From ISO returns time in browser timezone by default. So set the timezone of input time.
                newDateTime = DateTime.fromISO(newDatetimeISO, { setZone: true });
            } else {
                newDateTime = time;
            }

            if (newDateTime <= DateTime.now().minus({ minutes: 30 })) {
                if (newDateTime.isValid) {
                    updateTimelineStartTime(newDateTime);
                }

                setTimeInput(newDateTime);
            }
        }
    };

    const now = unitMetadata.recordingStart ? DateTime.now().setZone(unitMetadata.timezone) : DateTime.now();
    const TimelineDateDotConditionalMarginTop = videoSelectionModeActive ? "25px" : "0px";

    return (
        <div ref={headerRef}>
            {videoSelectionModeActive && <VideoRequestHeader />}

            <div className={classes.grid} ref={gridRef}>
                <div className={classes.note}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        NOTE:
                    </Typography>
                    <Typography variant="body2">
                        {canRequestVideo
                            ? `Unsaved footage before ${renderRecordingStart()} was deleted and is no longer available.`
                            : "This is an Archive Cabinet, and cannot request video."}
                    </Typography>
                </div>
                <div className={classes.inputs}>
                    <ThemeProvider theme={pickerStyles}>
                        <KeyboardDatePicker
                            className={FullStory.names.classes.history.timelineDate}
                            label="Date"
                            variant="inline"
                            format="MM/dd/yyyy"
                            value={dateInput}
                            onChange={handleDateChange}
                            onAccept={updateTimelineStartDate}
                            disableFuture={true}
                            maxDateMessage={"Invalid Date"}
                            onBlur={() => {
                                if (!dateInput?.isValid) setDateInput(timelineStart.plus({ minutes: 30 }));
                            }}
                        />
                        <KeyboardTimePicker
                            label="Time"
                            variant="inline"
                            placeholder="08:00 AM"
                            mask="__:__ _M"
                            value={timeInput}
                            onChange={handleTimeChange}
                            onAccept={updateTimelineStartTime}
                            format={"hh:mm a"}
                            keyboardIcon={<AccessTimeIcon />}
                            onBlur={() => {
                                if (!timeInput?.isValid) setTimeInput(timelineStart.plus({ minutes: 30 }));
                            }}
                            data-testid="time-picker"
                        />
                        <ThemeProvider theme={tzStyle}>
                            <TextField label="Time Zone" value={unitMetadata.timezone || DateTime.now().zoneName} />
                        </ThemeProvider>
                        {canRequestVideo ? (
                            <Tooltip
                                arrow
                                placement="bottom-end"
                                disableHoverListener={!outsideOfBounds()}
                                title={`The timeline must be within ${renderRecordingStart()} and ${now.toLocaleString(
                                    DateTime.DATETIME_FULL,
                                )} to select video`}
                            >
                                <span className={classes.requestVideoSpan}>
                                    <Button
                                        className={clsx(classes.requestVideo, FullStory.names.classes.history.selectVideo)}
                                        disableElevation
                                        onClick={openVideoSelection}
                                        variant="contained"
                                        disabled={outsideOfBounds()}
                                    >
                                        Select Video
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : (
                            <></>
                        )}
                    </ThemeProvider>
                </div>
                <div className={classes.timeline}>
                    <div className={classes.dotAndDate} style={{ marginTop: TimelineDateDotConditionalMarginTop }}>
                        <div className={classes.dot} style={{ backgroundColor: showStatus }} />
                        <Typography variant="body1">{timelineStart.toLocaleString(DateTime.DATE_FULL)}</Typography>
                    </div>
                    <ArchiveTimeline selectedTime={timeInput} />
                </div>
            </div>
        </div>
    );
}
